import {
  getFirestore,
  doc,
  setDoc,
  deleteDoc,
  collection,
  query,
  where,
  getDocs,
} from "firebase/firestore";
import { getAuth } from "firebase/auth";

const db = getFirestore();

const isAuthenticated = () => {
  try {
    if (getAuth().currentUser) return true;
  } catch (_) {
    // Auth may not be initialized yet
  }
  return localStorage.getItem("adminToken") !== null;
};

export const parseWithdrawalDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (typeof value.toDate === "function") {
    try {
      const parsed = value.toDate();
      return parsed instanceof Date && !Number.isNaN(parsed.getTime()) ? parsed : null;
    } catch (_) {
      return null;
    }
  }
  if (typeof value === "object") {
    const seconds = value.seconds ?? value._seconds;
    if (seconds != null) {
      const nanos = value.nanoseconds ?? value._nanoseconds ?? 0;
      const parsed = new Date(seconds * 1000 + nanos / 1e6);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    }
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const formatWithdrawalDate = (value) => {
  const parsed = parseWithdrawalDate(value);
  return parsed ? parsed.toLocaleDateString() : "N/A";
};

const endOfDay = (date) => {
  const next = new Date(date);
  next.setHours(23, 59, 59, 999);
  return next;
};

export const isWithdrawalInSeason = (withdrawal, season) => {
  if (!withdrawal || !season) return false;

  const seasonId = season.id || season.seasonId || null;
  if (withdrawal.seasonId) {
    return Boolean(seasonId) && withdrawal.seasonId === seasonId;
  }

  const withdrawnAt = parseWithdrawalDate(withdrawal.withdrawnAt);
  if (!withdrawnAt) return false;

  const start = parseWithdrawalDate(season.startDate);
  const end = parseWithdrawalDate(season.endDate);
  if (start && withdrawnAt.getTime() < start.getTime()) return false;
  if (end && withdrawnAt.getTime() > endOfDay(end).getTime()) return false;
  return Boolean(start || end);
};

export const filterWithdrawalsForSeason = (withdrawals, season) => {
  if (!Array.isArray(withdrawals)) return [];
  if (!season) return [];
  return withdrawals.filter((withdrawal) => isWithdrawalInSeason(withdrawal, season));
};

// Save a skater as withdrawn
export const withdrawSkater = async (level, skater, note, season = null) => {
  try {
    if (!isAuthenticated()) {
      throw new Error("User not authenticated");
    }

    const withdrawalData = {
      level,
      skaterId: `${level}-${skater.firstName}-${skater.lastName}`.replace(
        /\s+/g,
        "-"
      ),
      skaterName: skater.name,
      firstName: skater.firstName,
      lastName: skater.lastName,
      club: skater.club,
      coach: skater.coach,
      totalPoints: skater.totalPoints,
      competitions: skater.competitions,
      withdrawalNote: note,
      withdrawnAt: new Date(),
      withdrawnBy: 'admin', // Since we're using localStorage auth
      withdrawnByEmail: 'admin@pnicscores.com', // Default admin email
    };

    if (season?.id) {
      withdrawalData.seasonId = season.id;
    }
    if (season?.name) {
      withdrawalData.seasonName = season.name;
    }

    const withdrawalRef = doc(db, "withdrawnSkaters", withdrawalData.skaterId);
    await setDoc(withdrawalRef, withdrawalData);

    return withdrawalData;
  } catch (error) {
    console.error("Error withdrawing skater:", error);
    throw error;
  }
};

// Restore a withdrawn skater
export const restoreSkater = async (skaterId) => {
  try {
    if (!isAuthenticated()) {
      throw new Error("User not authenticated");
    }

    const withdrawalRef = doc(db, "withdrawnSkaters", skaterId);
    await deleteDoc(withdrawalRef);
    return true;
  } catch (error) {
    console.error("Error restoring skater:", error);
    throw error;
  }
};

// Update withdrawal note
export const updateWithdrawalNote = async (skaterId, note) => {
  try {
    if (!isAuthenticated()) {
      throw new Error("User not authenticated");
    }

    const withdrawalRef = doc(db, "withdrawnSkaters", skaterId);
    await setDoc(withdrawalRef, { withdrawalNote: note }, { merge: true });
    return true;
  } catch (error) {
    console.error("Error updating withdrawal note:", error);
    throw error;
  }
};

// Load withdrawn skaters for a specific level
export const loadWithdrawnSkaters = async (level, season = null) => {
  try {
    const withdrawnQuery = query(
      collection(db, "withdrawnSkaters"),
      where("level", "==", level)
    );

    const querySnapshot = await getDocs(withdrawnQuery);
    const withdrawnSkaters = [];

    querySnapshot.forEach((docSnap) => {
      const data = docSnap.data();
      withdrawnSkaters.push({
        id: docSnap.id,
        ...data,
        withdrawnAt: parseWithdrawalDate(data.withdrawnAt),
      });
    });

    return season
      ? filterWithdrawalsForSeason(withdrawnSkaters, season)
      : withdrawnSkaters;
  } catch (error) {
    console.error("Error loading withdrawn skaters:", error);
    return [];
  }
};

// Check if a skater is withdrawn
export const isSkaterWithdrawn = async (level, skaterName, season = null) => {
  try {
    const withdrawnQuery = query(
      collection(db, "withdrawnSkaters"),
      where("level", "==", level),
      where("skaterName", "==", skaterName)
    );

    const querySnapshot = await getDocs(withdrawnQuery);
    if (!season) {
      return !querySnapshot.empty;
    }

    return querySnapshot.docs.some((docSnap) =>
      isWithdrawalInSeason(
        {
          ...docSnap.data(),
          withdrawnAt: parseWithdrawalDate(docSnap.data().withdrawnAt),
        },
        season
      )
    );
  } catch (error) {
    console.error("Error checking withdrawal status:", error);
    return false;
  }
};

// Get all withdrawn skaters across all levels
export const getAllWithdrawnSkaters = async (season = null) => {
  try {
    const querySnapshot = await getDocs(collection(db, "withdrawnSkaters"));
    const withdrawnSkaters = [];

    querySnapshot.forEach((docSnap) => {
      const data = docSnap.data();
      withdrawnSkaters.push({
        id: docSnap.id,
        ...data,
        withdrawnAt: parseWithdrawalDate(data.withdrawnAt),
      });
    });

    return season
      ? filterWithdrawalsForSeason(withdrawnSkaters, season)
      : withdrawnSkaters;
  } catch (error) {
    console.error("Error loading all withdrawn skaters:", error);
    throw error;
  }
};
